import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";

export default function SearchOverlay({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); setTotalCount(0); return; }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setTotalCount(0); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const [{ data }, { data: c }] = await Promise.all([
          api.get("/products", { params: { q, limit: 8 } }),
          api.get("/products/count", { params: { q } }),
        ]);
        setResults(data.slice(0, 6));
        setTotalCount(c.count || 0);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const goToResults = () => {
    if (!q.trim()) return;
    onOpenChange(false);
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl top-[10%] translate-y-0 rounded-none p-0 gap-0" data-testid="search-overlay">
        <DialogTitle className="sr-only">Buscar</DialogTitle>
        <DialogDescription className="sr-only">Busca perfumes por marca, familia olfativa o notas.</DialogDescription>
        <div className="flex items-center border-b px-6 py-4 gap-4">
          <Search strokeWidth={1.25} className="h-5 w-5" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); goToResults(); }
            }}
            placeholder="Buscar por marca, familia, notas… (pulsa Enter para todos los resultados)"
            className="flex-1 bg-transparent outline-none font-serif text-2xl placeholder:text-muted-foreground"
            data-testid="search-input"
          />
          <button onClick={() => onOpenChange(false)} className="text-muted-foreground hover:text-foreground" data-testid="search-close">
            <X strokeWidth={1.25} className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-auto">
          {q && loading && <div className="p-6 text-sm text-muted-foreground">Buscando…</div>}
          {q && !loading && results.length === 0 && (
            <div className="p-6 space-y-2">
              <div className="font-serif text-lg">Sin coincidencias exactas para "{q}"</div>
              <div className="text-sm text-muted-foreground">Prueba con otra ortografía o abre todos los resultados abajo.</div>
            </div>
          )}
          {results.length > 0 && (
            <>
              <div className="px-6 pt-4 pb-2 font-mono-caps text-[11px] text-muted-foreground" data-testid="search-summary">
                {totalCount.toLocaleString()} resultado{totalCount === 1 ? "" : "s"} · mostrando primeros {results.length}
              </div>
              <ul className="divide-y">
                {results.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/producto/${p.slug}`}
                      onClick={() => onOpenChange(false)}
                      className="flex items-center gap-4 p-4 hover:bg-muted transition-colors"
                      data-testid={`search-result-${p.slug}`}
                    >
                      <img src={p.images[0]} alt="" className="w-14 h-16 object-cover bg-muted" />
                      <div className="flex-1">
                        <div className="font-mono-caps text-xs text-muted-foreground">{p.brand}</div>
                        <div className="font-serif text-lg">{p.name}</div>
                      </div>
                      <div className="text-sm">Desde ${Math.min(...p.sizes.map(s=>s.price)).toFixed(2)}</div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {q && !loading && (
            <button
              onClick={goToResults}
              className="w-full flex items-center justify-center gap-3 px-6 py-5 bg-navy text-cream font-mono-caps hover:bg-[#8B0000] transition-colors border-t"
              data-testid="search-see-all"
            >
              Ver todos los resultados{totalCount > 0 ? ` (${totalCount.toLocaleString()})` : ""}
              <ArrowRight strokeWidth={1.25} className="h-4 w-4" />
            </button>
          )}
          {!q && (
            <div className="p-6 space-y-4">
              <div className="font-mono-caps text-muted-foreground">Búsquedas populares</div>
              <div className="flex flex-wrap gap-2">
                {["Oud", "Vainilla", "Vetiver", "Rosa", "Neroli", "Ámbar", "Chanel", "Dior"].map((t) => (
                  <button key={t} onClick={() => setQ(t)} className="border border-border px-4 py-2 font-mono-caps text-xs hover:border-navy hover:text-navy transition-colors" data-testid={`search-suggestion-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
